import React, { useState, useEffect, useCallback, useRef } from "react";
import { useWallet } from "@/context/WalletContext";
import { useSuiClient, useSignAndExecuteTransaction } from "@mysten/dapp-kit";
import { toast } from "sonner";
import { lockTokens, unlockTokens, getTokenBalance } from "@/lib/sui";
import { api, shortAddr } from "@/lib/api";
import { fmtSui, fmtNum, timeAgo } from "@/lib/format";
import { useLive } from "@/context/LiveContext";
import { liveDebounceMs, isHidden } from "@/lib/perf";

function countdown(ms) {
  const d = ms - Date.now();
  if (d <= 0) return "unlocked";
  const days = Math.floor(d / 86400000);
  const hrs = Math.floor((d % 86400000) / 3600000);
  if (days > 0) return `${days}d ${hrs}h left`;
  const mins = Math.floor((d % 3600000) / 60000);
  return `${hrs}h ${mins}m left`;
}

function Pager({ page, totalPages, onChange }) {
  if (totalPages <= 1) return null;
  // Compact windowed page numbers + Next, consistent with the app's pill styling.
  let nums = [];
  if (totalPages <= 5) nums = Array.from({ length: totalPages }, (_, i) => i + 1);
  else {
    const s = Math.min(Math.max(page - 1, 1), totalPages - 3);
    nums = [s, s + 1, s + 2, s + 3].filter((n) => n <= totalPages);
    if (nums[0] > 1) nums = [1, ...nums];
  }
  return (
    <div className="flex items-center gap-1.5 mt-3 flex-wrap" data-testid="pagination">
      {nums.map((n) => (
        <button key={n} onClick={() => onChange(n)} data-testid={`page-${n}`}
          className={`min-w-[28px] h-7 px-2 rounded-md text-xs font-bold font-mono border transition-colors ${n === page ? "border-[#1EC62E]/60 text-[#1EC62E] bg-[#1EC62E]/10" : "border-white/10 text-muted-foreground hover:text-white hover:border-white/20"}`}>
          {n}
        </button>
      ))}
      <button onClick={() => onChange(Math.min(page + 1, totalPages))} disabled={page >= totalPages} data-testid="page-next"
        className="h-7 px-2.5 rounded-md text-xs font-bold border border-white/10 text-muted-foreground hover:text-white hover:border-white/20 disabled:opacity-30 disabled:hover:text-muted-foreground transition-colors">
        Next
      </button>
    </div>
  );
}

export default function TradesHolders({ coin }) {
  const [tab, setTab] = useState("trades");
  const [tradePage, setTradePage] = useState(1);
  const [holderPage, setHolderPage] = useState(1);
  const [trades, setTrades] = useState({ items: [], total: 0, total_pages: 1 });
  const [holders, setHolders] = useState({ items: [], total: 0, total_pages: 1 });
  const [vesting, setVesting] = useState({ locks: [], dev_locked_tokens: 0, dev_lock_days: 0, total_locked: 0 });
  const [vestPkg, setVestPkg] = useState(null);
  const [lockAmt, setLockAmt] = useState("");
  const [lockDays, setLockDays] = useState(30);
  const [tokenBal, setTokenBal] = useState(0);
  const [busy, setBusy] = useState(false);
  const { address, connected } = useWallet();
  const client = useSuiClient();
  const { mutateAsync: signExec } = useSignAndExecuteTransaction();
  const { subscribe } = useLive();
  const isDev = connected && address && coin.creator && address.toLowerCase() === coin.creator.toLowerCase();

  const loadVesting = useCallback(async () => {
    try {
      const { data } = await api.get(`/coins/onchain/${coin.curve_id}/vesting`, { params: { coin_type: coin.coin_type, creator: coin.creator, decimals: coin.decimals || 9 } });
      setVesting(data);
    } catch (e) {}
  }, [coin.curve_id, coin.coin_type, coin.creator]);

  const loadBal = useCallback(async () => {
    if (!address) { setTokenBal(0); return; }
    try {
      const base = await getTokenBalance(client, { owner: address, coinType: coin.coin_type });
      setTokenBal(Number(base) / 10 ** (coin.decimals || 9));
    } catch (e) { setTokenBal(0); }
  }, [client, address, coin.coin_type, coin.decimals]);

  useEffect(() => { api.get("/vesting/config").then(({ data }) => setVestPkg(data.vesting_package_id)).catch(() => {}); }, []);
  useEffect(() => { if (tab === "vesting") { loadVesting(); loadBal(); } }, [tab, loadVesting, loadBal]);

  const doLock = async () => {
    const amt = parseFloat(lockAmt);
    if (!amt || amt <= 0) { toast.error("Enter an amount"); return; }
    if (amt > tokenBal) { toast.error(`You only have ${fmtNum(tokenBal)} ${coin.symbol}`); return; }
    setBusy(true);
    try {
      const base = BigInt(Math.floor(amt * 10 ** (coin.decimals || 9)));
      await lockTokens(signExec, client, { vestingPkg: vestPkg, coinType: coin.coin_type, amountBase: base, days: lockDays, isDev }, address);
      toast.success(`Locked ${amt} ${coin.symbol} for ${lockDays} days`);
      setLockAmt(""); setTimeout(() => { loadVesting(); loadBal(); }, 2500);
    } catch (e) { toast.error(e?.message?.slice(0, 80) || "Lock failed"); }
    finally { setBusy(false); }
  };

  const doUnlock = async (lock) => {
    setBusy(true);
    try {
      await unlockTokens(signExec, client, { vestingPkg: vestPkg, coinType: coin.coin_type, lockId: lock.lock_id });
      toast.success("Unlocked & returned to your wallet");
      setTimeout(() => { loadVesting(); loadBal(); }, 2500);
    } catch (e) { toast.error(e?.message?.slice(0, 80) || "Unlock failed"); }
    finally { setBusy(false); }
  };

  const params = {
    package_id: coin.package_id, creator: coin.creator,
    decimals: coin.decimals || 9, page_size: 10,
  };

  const loadTrades = useCallback(async (page) => {
    try {
      const { data } = await api.get(`/coins/onchain/${coin.curve_id}/trades`, { params: { ...params, page } });
      setTrades(data);
    } catch (e) {}
  }, [coin.curve_id, coin.package_id]);

  const loadHolders = useCallback(async (page) => {
    try {
      const { data } = await api.get(`/coins/onchain/${coin.curve_id}/holders`, { params: { ...params, page } });
      setHolders(data);
    } catch (e) {}
  }, [coin.curve_id, coin.package_id]);

  useEffect(() => { if (tab === "trades") loadTrades(tradePage); }, [tab, tradePage, loadTrades]);
  useEffect(() => { if (tab === "holders") loadHolders(holderPage); }, [tab, holderPage, loadHolders]);

  // Real-time: refresh the active tab so new trades / balance changes appear automatically.
  useEffect(() => {
    const t = setInterval(() => {
      if (isHidden()) return;
      if (tab === "trades") loadTrades(tradePage);
      else if (tab === "holders") loadHolders(holderPage);
    }, 25000);
    return () => clearInterval(t);
  }, [tab, tradePage, holderPage, loadTrades, loadHolders]);

  // Live WS: a trade on this curve refreshes the visible tab (debounced, mobile-aware).
  const liveTimer = useRef(null);
  useEffect(() => {
    return subscribe((msg) => {
      if (msg.type !== "trade" || msg.curve_id !== coin.curve_id || isHidden()) return;
      if (liveTimer.current) clearTimeout(liveTimer.current);
      liveTimer.current = setTimeout(() => {
        if (tab === "trades") loadTrades(tradePage);
        else if (tab === "holders") loadHolders(holderPage);
      }, Math.min(liveDebounceMs, 2500));
    });
  }, [subscribe, coin.curve_id, tab, tradePage, holderPage, loadTrades, loadHolders]);

  return (
    <div className="card-surface p-5" data-testid="trades-holders">
      <div className="seg mb-4" data-testid="trades-holders-tabs">
        <button onClick={() => setTab("trades")} data-testid="tab-trades" data-active={tab === "trades"} className="seg-tab">
          Trades {trades.total ? <span className="opacity-60">({trades.total})</span> : null}
        </button>
        <button onClick={() => setTab("holders")} data-testid="tab-holders" data-active={tab === "holders"} className="seg-tab">
          Holders {holders.total ? <span className="opacity-60">({holders.total})</span> : null}
        </button>
        <button onClick={() => setTab("vesting")} data-testid="tab-vesting" data-active={tab === "vesting"} className="seg-tab">
          Vesting {vesting.locks?.length ? <span className="opacity-60">({vesting.locks.length})</span> : null}
        </button>
      </div>

      {tab === "vesting" ? (
        <div data-testid="vesting-panel">
          {vesting.dev_locked_tokens > 0 && (
            <div className="rounded-xl border border-[#FFB800]/30 bg-[#FFB800]/5 px-4 py-2.5 mb-4 text-xs font-mono flex items-center gap-2" data-testid="dev-lock-banner">
              <span>🔒</span><span className="text-[#FFB800] font-bold">Dev locked {fmtNum(vesting.dev_locked_tokens)} {coin.symbol}</span>
              <span className="text-muted-foreground">for {vesting.dev_lock_days} days</span>
            </div>
          )}
          {connected && vestPkg && (
            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4 mb-4">
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-bold mb-2">Lock your {coin.symbol}{isDev ? " (dev)" : ""}</div>
              <div className="flex items-center justify-between mb-1.5 text-[11px] font-mono" data-testid="vesting-balance">
                <span className="text-muted-foreground">Available: <span className="text-white font-bold">{fmtNum(tokenBal)}</span> {coin.symbol}</span>
                <button type="button" onClick={() => setLockAmt(String(tokenBal))} disabled={tokenBal <= 0} data-testid="lock-max" className="px-2 py-0.5 rounded bg-[#1EC62E]/15 text-[#1EC62E] font-bold border border-[#1EC62E]/40 disabled:opacity-30">Max</button>
              </div>
              <input value={lockAmt} onChange={(e) => setLockAmt(e.target.value.replace(/[^0-9.]/g, ""))} placeholder={`Amount of ${coin.symbol}`} data-testid="lock-amount-input" className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm font-mono mb-2 focus:outline-none focus:border-[#1EC62E]/50" />
              <div className="flex items-center gap-2 mb-3">
                {[7, 30, 60].map((d) => (
                  <button key={d} onClick={() => setLockDays(d)} data-testid={`lock-days-${d}`} className={`flex-1 py-2 rounded-lg text-xs font-bold border transition-colors ${lockDays === d ? "border-[#1EC62E]/60 text-[#1EC62E] bg-[#1EC62E]/10" : "border-white/10 text-muted-foreground"}`}>{d} days</button>
                ))}
              </div>
              <button onClick={doLock} disabled={busy} data-testid="lock-submit" className="w-full py-2.5 rounded-lg font-bold text-black disabled:opacity-40" style={{ background: "linear-gradient(90deg,#1EC62E,#7CF24E)" }}>{busy ? "Locking…" : `Lock for ${lockDays} days`}</button>
              <p className="text-[10px] text-muted-foreground mt-2">Tokens are held safely in the vesting contract and can only return to your wallet after the lock ends. No admin can touch them.</p>
            </div>
          )}
          <div className="space-y-2" data-testid="vesting-list">
            {(vesting.locks || []).map((l) => (
              <div key={l.lock_id} className="flex items-center gap-3 text-xs font-mono border-t border-white/5 py-2">
                <a href={`https://suiscan.xyz/mainnet/account/${l.owner}`} target="_blank" rel="noreferrer" className="hover:text-[#1EC62E] flex items-center gap-1.5 w-[110px] shrink-0">
                  {shortAddr(l.owner)}
                  {l.is_dev && <span className="px-1 rounded bg-[#FFB800]/20 text-[#FFB800] text-[9px] font-bold">DEV</span>}
                </a>
                <span className="flex-1">{fmtNum(l.amount)} <span className="text-muted-foreground">· {l.days}d</span></span>
                <span className={l.claimable ? "text-[#1EC62E]" : "text-muted-foreground"}>{countdown(l.unlock_ms)}</span>
                {l.claimable && address && l.owner.toLowerCase() === address.toLowerCase() && (
                  <button onClick={() => doUnlock(l)} disabled={busy} data-testid={`unlock-${l.lock_id}`} className="px-2 py-1 rounded bg-[#1EC62E]/15 text-[#1EC62E] text-[10px] font-bold border border-[#1EC62E]/40">Unlock</button>
                )}
              </div>
            ))}
            {(vesting.locks || []).length === 0 && <div className="py-6 text-center text-muted-foreground text-sm">No tokens locked yet</div>}
          </div>
        </div>
      ) : tab === "trades" ? (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-xs font-mono">
              <thead><tr className="text-muted-foreground text-left"><th className="py-1.5">Account</th><th>Type</th><th className="text-right">SUI</th><th className="text-right hidden sm:table-cell">{coin.symbol}</th><th className="text-right">Time</th></tr></thead>
              <tbody data-testid="trades-list">
                {trades.items.map((t, i) => (
                  <tr key={t.digest || i} className="border-t border-white/5">
                    <td className="py-2">
                      <a href={`https://suiscan.xyz/mainnet/tx/${t.digest}`} target="_blank" rel="noreferrer" className="hover:text-[#1EC62E] inline-flex items-center gap-1.5">
                        {shortAddr(t.trader)}
                        {t.trader === coin.creator && <span className="px-1 rounded bg-[#FFB800]/20 text-[#FFB800] text-[9px] font-bold">DEV</span>}
                      </a>
                    </td>
                    <td><span className={t.is_buy ? "text-[#22C55E]" : "text-[#F43F5E]"}>{t.is_buy ? "buy" : "sell"}</span></td>
                    <td className="text-right">{fmtSui(t.sui_amount, 3)}</td>
                    <td className="text-right hidden sm:table-cell">{fmtNum(t.token_amount)}</td>
                    <td className="text-right text-muted-foreground">{t.ts ? timeAgo(new Date(t.ts).toISOString()) : "—"}</td>
                  </tr>
                ))}
                {trades.items.length === 0 && <tr><td colSpan={5} className="py-6 text-center text-muted-foreground">No trades yet</td></tr>}
              </tbody>
            </table>
          </div>
          <Pager page={trades.page || tradePage} totalPages={trades.total_pages || 1} onChange={setTradePage} />
        </>
      ) : (
        <>
          <div className="space-y-1.5" data-testid="holders-list">
            {holders.items.map((h, i) => {
              const rank = ((holders.page || 1) - 1) * 10 + i + 1;
              const pct = coin.total_supply ? (h.balance / coin.total_supply) * 100 : 0;
              return (
                <div key={h.address || i} className="flex items-center gap-3 text-xs font-mono">
                  <span className="w-6 text-muted-foreground text-right">{rank}</span>
                  <a href={`https://suiscan.xyz/mainnet/account/${h.address}`} target="_blank" rel="noreferrer" className="hover:text-[#1EC62E] flex items-center gap-1.5 w-[110px] shrink-0">
                    {shortAddr(h.address)}
                    {h.is_dev && <span className="px-1 rounded bg-[#FFB800]/20 text-[#FFB800] text-[9px] font-bold" data-testid="holder-dev-tag">DEV</span>}
                  </a>
                  <div className="flex-1 h-1.5 rounded-full bg-white/5 overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${Math.min(pct, 100)}%`, background: h.is_dev ? "#FFB800" : "linear-gradient(90deg,#1EC62E,#7CF24E)" }} />
                  </div>
                  <span className="w-14 text-right text-muted-foreground">{pct.toFixed(2)}%</span>
                </div>
              );
            })}
            {holders.items.length === 0 && <div className="py-6 text-center text-muted-foreground text-sm">No holders yet</div>}
          </div>
          <Pager page={holders.page || holderPage} totalPages={holders.total_pages || 1} onChange={setHolderPage} />
        </>
      )}
    </div>
  );
}
